import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const groupSlots = (slots) => {
  const days = {};
  slots.forEach((s) => {
    const d = new Date(s);
    const key = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    (days[key] = days[key] || []).push(s);
  });
  return days;
};

export default function Reschedule() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(null);
  const [cal, setCal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelled, setCancelled] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    fetch(`${API}/reschedule/${token}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "This link isn't active anymore.");
        setInfo(d);
      })
      .catch((e) => setError(e.message));
  }, [token]);

  const confirm = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/reschedule/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slot: selected }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "That didn't work — please try again.");
      setDone(d.message);
      setCal(d.calendar || null);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/reschedule/${token}/cancel`, { method: "POST" });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "That didn't work — please try again.");
      setCancelled(d.message);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="reschedule-page">
      <header className="border-b border-[#2E2E2E] px-6 h-16 flex items-center gap-3">
        <span className="font-display text-lg text-white">DANE/OS</span>
        <span className="text-base font-semibold text-[#D6D6D6] ml-auto">Scheduling</span>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-xl rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 md:p-10 space-y-6">
          {error && !done && !cancelled ? (
            <div data-testid="reschedule-error">
              <p className="text-base font-bold text-[#FF5C5C] mb-3">This link isn't active</p>
              <p className="text-base font-medium text-[#D6D6D6] leading-[1.6]">{error}</p>
              <p className="text-base font-medium text-[#D6D6D6] mt-3 leading-[1.6]">If your call is still on, just show up. Otherwise, start a new conversation on the main page.</p>
            </div>
          ) : cancelled ? (
            <div data-testid="reschedule-cancelled" className="space-y-3">
              <p className="text-base font-bold text-white">Cancelled</p>
              <p className="text-base font-medium text-[#D6D6D6] leading-[1.6]">{cancelled}</p>
            </div>
          ) : done ? (
            <div data-testid="reschedule-success" className="space-y-3">
              <p className="text-base font-bold text-[#4ADE80]">You're all set</p>
              <p className="text-base font-medium text-[#D6D6D6] leading-[1.6]">{done}</p>
              {cal && (
                <div className="flex flex-wrap gap-3 pt-1">
                  <a href={cal.gcal_url} target="_blank" rel="noreferrer" data-testid="reschedule-gcal-link"
                    className="rounded-xl bg-white text-black font-semibold text-base px-4 py-2.5 hover:bg-[#D6D6D6] transition-colors duration-150">
                    Add to Google Calendar
                  </a>
                  <a href={`${API}/calendar/invite/${cal.ics_token}.ics`} data-testid="reschedule-ics-link"
                    className="rounded-xl border border-[#2E2E2E] text-[#D6D6D6] font-semibold text-base px-4 py-2.5 hover:text-white transition-colors duration-150">
                    Any other calendar (.ics)
                  </a>
                </div>
              )}
            </div>
          ) : !info ? (
            <p className="text-base font-medium text-[#D6D6D6] animate-pulse">Pulling up your booking...</p>
          ) : (
            <>
              <div>
                <p className="text-base font-bold text-[#FF5C5C] mb-3">Move your call</p>
                <p className="text-base font-medium text-[#D6D6D6] leading-[1.6]">
                  Hi {info.name} — your call is currently set for <span className="text-white font-bold">{info.slot_label}</span>. Pick a new time below and you're done.
                </p>
              </div>
              <div className="space-y-4 max-h-[40vh] overflow-y-auto chat-scroll pr-1">
                {Object.entries(groupSlots(info.slots)).map(([day, daySlots]) => (
                  <div key={day}>
                    <p className="text-base font-semibold text-[#D6D6D6] mb-2">{day}</p>
                    <div className="flex flex-wrap gap-2">
                      {daySlots.map((s) => (
                        <button key={s} onClick={() => setSelected(s)} data-testid="reschedule-slot"
                          className={`text-base font-medium px-4 py-2.5 rounded-xl border transition-colors duration-150 ${selected === s ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-[#2E2E2E] text-[#D6D6D6] hover:border-[#FF5C5C]"}`}>
                          {new Date(s).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={confirm} disabled={!selected || busy} data-testid="reschedule-confirm-button"
                className="w-full rounded-xl bg-white text-black font-semibold text-base px-8 py-3.5 hover:bg-[#D6D6D6] disabled:opacity-30 transition-colors duration-150">
                {busy ? "Moving it..." : "Confirm the new time"}
              </button>
              <div className="border-t border-[#2E2E2E] pt-4">
                {!confirmCancel ? (
                  <button onClick={() => setConfirmCancel(true)} data-testid="reschedule-cancel-button"
                    className="text-base font-medium text-[#D6D6D6] hover:text-[#FF5C5C] transition-colors duration-150 underline underline-offset-4">
                    Can't make a call at all? You can cancel instead.
                  </button>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-base font-medium text-[#D6D6D6]">Are you sure? This frees up the slot.</p>
                    <button onClick={cancel} disabled={busy} data-testid="reschedule-cancel-confirm"
                      className="text-base font-semibold rounded-xl border border-[#FF5C5C] text-[#FF5C5C] px-5 py-2.5 hover:bg-[#DC2626] hover:border-[#DC2626] hover:text-white disabled:opacity-40 transition-colors duration-150">
                      {busy ? "Cancelling..." : "Yes, cancel it"}
                    </button>
                    <button onClick={() => setConfirmCancel(false)} data-testid="reschedule-cancel-abort"
                      className="text-base font-semibold text-[#D6D6D6] hover:text-white transition-colors duration-150">
                      Keep the call
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
